import React, { useEffect, useState } from "react";
import {
    Button,
    TextField,
    IconButton,
    Typography
} from "@mui/material";
import { Send as SendIcon } from '@mui/icons-material';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { sendSupportEmail } from "../network/apis/email.api";
import { useAuth } from "../context/authContext";
import DOMPurify from "dompurify";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import { resendEmail } from "../network/apis/auth.api";
import { useProfile } from "../context/userContext";
import { useAuth0 } from "@auth0/auth0-react";
import { isEmailVerifiedOperation } from "../network/apis/operation.api";
import Link from "@mui/material/Link";

const ContactUs = () => {
    const { authenticated, token } = useAuth();
    const [formData, setFormData] = useState({ User_name: "", User_email: "", message: "" });
    const [errors, setErrors] = useState({});
    const [captchaValue, setCaptchaValue] = useState("");
    const [submissionStatus, setSubmissionStatus] = useState("");
    const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
    const [verifyLinkText, setVerifyLinkText] = useState("Verify Now");
    const [isUserEmailVerified, setIsUserEmailVerified] = useState(false);
    const { profile } = useProfile();
    const { loginWithRedirect, logout } = useAuth0();

    const verifyEmail = async (email) => {
        try {
            setVerifyLinkText("Sending Email...");
            const response = await resendEmail(profile.email, profile.userId);
            if (response.status === 200) {
                setVerifyLinkText("Email Sent");
            } else {
                setVerifyLinkText("Error Sending Email");
            }
        } catch (error) {
            alert("An error occurred while verifying your email.");
            //TODO: log application error with email
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.User_name.trim()) newErrors.User_name = "Name is required";
        if (!formData.User_email.trim()) newErrors.User_email = "Email is required";
        if (!formData.message.trim()) newErrors.message = "Message is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCaptchaChange = (value) => {
        setCaptchaValue(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const sanitizedMessage = DOMPurify.sanitize(formData.message);
        if (!captchaValue) { alert("Please complete the reCAPTCHA"); return; }
        if (validateForm()) {
            const emailDetails = { userEmail: formData.User_email, userMessage: sanitizedMessage };
            try {
                await sendSupportEmail(token, emailDetails);
                setSubmissionStatus("Success! Your message has been sent.");
                setFormData({ User_name: "", User_email: "", message: "" });
                setCaptchaValue("");
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    setSubmissionStatus("Error: Please login to send a message.");
                } else {
                    setSubmissionStatus("Error: Failed to send your message. Please contact us on support@flyzone.io");
                }
            }
        }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const isEmailVerified = async (email) => {
        try {
            const is_verified = await isEmailVerifiedOperation(token, email);
            setIsUserEmailVerified(is_verified);
        } catch (error) {
        }
    };

    useEffect(() => {
        setRefreshReCaptcha(true);
        if (profile && profile.email) {
            isEmailVerified(profile.email);
            setFormData(prevData => ({
                ...prevData,
                User_name: `${profile.firstName} ${profile.lastName}`,
                User_email: profile.email,
                message: ""
            }));
        }
    }, [profile]);

    const recaptchaKey = process.env.REACT_APP_GOOGLE_RECAPTCHA;

    return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #f5f5f5 100%)' }}>
            <GoogleReCaptchaProvider
                reCaptchaKey={recaptchaKey || ''}
                scriptProps={{ async: true, defer: true, appendTo: 'head', nonce: undefined }}
                container={{ element: undefined, parameters: { badge: 'bottomright', theme: 'light' } }}
            >
                {/* ── Hero Banner ───────────────────────────── */}
                <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-blue-900 py-16 px-6 text-center relative overflow-hidden">
                    {/* Background circles decoration */}
                    <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-60 h-60 bg-white opacity-5 rounded-full translate-x-1/3 translate-y-1/3" />

                    <div className="relative z-10 max-w-2xl mx-auto">
                        {/* Avatar */}
                        <div className="w-16 h-16 mx-auto mb-5 bg-white bg-opacity-20 border-2 border-white border-opacity-30 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                                    d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M6.343 17.657a9 9 0 010-12.728M9.172 15.536a5 5 0 010-7.072M12 12h.01" />
                            </svg>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight drop-shadow">
                            Contact Support
                        </h1>
                        <p className="text-blue-100 text-lg mb-5">
                            Have questions or need assistance? Our support team is here to help.
                        </p>
                        <span className="inline-block bg-white bg-opacity-15 border border-white border-opacity-30 text-white text-sm font-medium px-4 py-1.5 backdrop-blur-sm">
                            ⏱ We typically respond within 24 hours
                        </span>
                    </div>
                </div>

                {/* ── Main Content ─────────────────────────── */}
                <div className="max-w-6xl mx-auto px-4 py-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">

                        {/* ── Left: Info Cards ─────────── */}
                        <div className="flex flex-col gap-5">

                            {/* Email Card */}
                            <div className="bg-white border border-blue-100 shadow-sm flex items-center gap-4 p-6 hover:shadow-md transition-shadow">
                                <div className="w-12 h-12 bg-blue-50 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900 text-sm mb-0.5">Email Support</p>
                                    <a href="mailto:support@flyzone.io"
                                        className="text-blue-700 font-medium text-sm hover:underline">
                                        support@flyzone.io
                                    </a>
                                </div>
                            </div>

                            {/* Social Card */}
                            <div className="bg-white border border-blue-100 shadow-sm p-6 hover:shadow-md transition-shadow">
                                <p className="font-bold text-gray-900 mb-1">Connect with us</p>
                                <p className="text-gray-500 text-sm mb-4">Follow us for updates & announcements</p>
                                <div className="flex gap-3">
                                    <a href="https://www.linkedin.com/company/flightloop"
                                        target="_blank" rel="noopener noreferrer"
                                        className="w-10 h-10 bg-blue-50 text-blue-700 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-all duration-200">
                                        <LinkedInIcon fontSize="small" />
                                    </a>
                                    <a href="https://www.facebook.com/profile.php?id=61576263661981"
                                        target="_blank" rel="noopener noreferrer"
                                        className="w-10 h-10 bg-blue-50 text-blue-700 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-all duration-200">
                                        <FacebookIcon fontSize="small" />
                                    </a>
                                </div>
                            </div>

                            {/* Quick Answers Card */}
                            <div className="bg-gradient-to-br from-blue-700 to-blue-900 text-white p-6 flex-1 flex flex-col shadow-sm">
                                <h2 className="text-xl font-bold mb-1">Looking for Quick Answers?</h2>
                                <p className="text-blue-100 mb-5">Browse our help resources before contacting support.</p>
                                <div className="flex flex-col gap-2 mt-auto">
                                    {[
                                        { label: 'View FAQs', href: '/help/faqs' },
                                        { label: 'User Guides', href: '/help/guides' },
                                        { label: 'Getting Started', href: '/help/getting-started' },
                                    ].map(({ label, href }) => (
                                        <button
                                            key={href}
                                            onClick={() => window.location.href = href}
                                            className="w-full text-left border border-white border-opacity-40 text-white text-sm font-medium px-4 py-2.5 hover:bg-white hover:bg-opacity-15 hover:border-white transition-all duration-200"
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ── Right: Form Card ─────────── */}
                        <div className="bg-white border border-blue-100 shadow-sm flex flex-col p-8 md:p-10">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Send us a message</h2>
                            <p className="text-gray-500 text-sm mb-6">Fill out the form below and we'll get back to you shortly.</p>

                            {!authenticated ? (
                                <div className="flex flex-col items-center justify-center flex-1 py-10 text-center">
                                    <div className="w-16 h-16 bg-blue-50 flex items-center justify-center mb-4">
                                        <svg className="w-8 h-8 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                                                d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M6.343 17.657a9 9 0 010-12.728M9.172 15.536a5 5 0 010-7.072M12 12h.01" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">Please log in to contact support</h3>
                                    <p className="text-gray-500 text-sm mb-6">You need to be logged in to send us a message.</p>
                                    <button
                                        onClick={() => loginWithRedirect()}
                                        className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 flex items-center gap-2 transition-colors duration-200 shadow-md"
                                    >
                                        <SendIcon fontSize="small" />
                                        Log In
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="flex flex-col flex-1">
                                    {submissionStatus.startsWith("Success") ? (
                                        <div className="flex flex-col items-center justify-center flex-1 py-10 text-center">
                                            <div className="w-16 h-16 bg-green-50 flex items-center justify-center mb-4">
                                                <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">Thank you for your message!</h3>
                                            <p className="text-gray-500 text-sm mb-6">We will get back to you as soon as possible.</p>
                                            <button
                                                type="button"
                                                onClick={() => setSubmissionStatus("")}
                                                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 transition-colors duration-200"
                                            >
                                                Send Another Message
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-5">
                                            {/* Name */}
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
                                                <TextField
                                                    name="User_name"
                                                    value={formData.User_name}
                                                    onChange={handleInputChange}
                                                    error={!!errors.User_name}
                                                    helperText={errors.User_name}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="medium"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 0,
                                                            bgcolor: '#f8faff',
                                                            '&:hover fieldset': { borderColor: '#1d4ed8' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1d4ed8', borderWidth: 2 },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': { color: '#1d4ed8' },
                                                    }}
                                                />
                                            </div>

                                            {/* Email verification warning */}
                                            {authenticated && !isUserEmailVerified && (
                                                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                                                    <span className="text-amber-500 text-lg leading-none">⚠</span>
                                                    <span>
                                                        Please verify your email address to proceed.&nbsp;
                                                        {verifyLinkText === "Verify Now" && (
                                                            <button
                                                                type="button"
                                                                onClick={() => verifyEmail(profile.email)}
                                                                className="text-blue-700 font-bold hover:underline"
                                                            >
                                                                {verifyLinkText}
                                                            </button>
                                                        )}
                                                        {verifyLinkText !== "Verify Now" && (
                                                            <span className="text-green-700 font-bold">{verifyLinkText}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Email */}
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
                                                <TextField
                                                    name="User_email"
                                                    value={formData.User_email}
                                                    onChange={handleInputChange}
                                                    error={!!errors.User_email}
                                                    helperText={errors.User_email}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="medium"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 0,
                                                            bgcolor: '#f8faff',
                                                            '&:hover fieldset': { borderColor: '#1d4ed8' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1d4ed8', borderWidth: 2 },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': { color: '#1d4ed8' },
                                                    }}
                                                />
                                            </div>

                                            {/* Message */}
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1">Your Message</label>
                                                <TextField
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    error={!!errors.message}
                                                    helperText={errors.message}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="medium"
                                                    multiline
                                                    rows={5}
                                                    placeholder="Describe your issue or question in detail..."
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 0,
                                                            bgcolor: '#f8faff',
                                                            '&:hover fieldset': { borderColor: '#1d4ed8' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1d4ed8', borderWidth: 2 },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': { color: '#1d4ed8' },
                                                    }}
                                                />
                                            </div>

                                            {/* reCAPTCHA */}
                                            <GoogleReCaptcha
                                                onVerify={handleCaptchaChange}
                                                refreshReCaptcha={refreshReCaptcha}
                                            />

                                            {/* Submit */}
                                            <button
                                                type="submit"
                                                disabled={
                                                    !captchaValue ||
                                                    !formData.User_name.trim() ||
                                                    !isValidEmail(formData.User_email) ||
                                                    !formData.message.trim() ||
                                                    !isUserEmailVerified
                                                }
                                                className="w-full py-4 bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold text-base flex items-center justify-center gap-2 transition-colors duration-200 shadow-md"
                                            >
                                                <SendIcon fontSize="small" />
                                                Submit
                                            </button>
                                        </div>
                                    )}
                                </form>
                            )}

                            {/* Status Banner */}
                            {submissionStatus && (
                                <div className={`mt-5 px-4 py-3 text-sm font-semibold text-center ${submissionStatus.startsWith("Success")
                                    ? 'bg-green-50 text-green-800 border border-green-200'
                                    : 'bg-red-50 text-red-800 border border-red-200'
                                    }`}>
                                    {submissionStatus}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </GoogleReCaptchaProvider>
        </div>
    );
};

export default ContactUs;